import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { useStorePanelTheme } from './useStorePanelTheme';
import { getCostPanelTheme } from './storePanelTheme';
import { createAdminPanelMuiTheme } from './adminPanelMuiTheme';

const AdminPanelThemeContext = createContext(null);

export { AdminPanelThemeContext };

export function AdminPanelThemeProvider({ children }) {
  const { mode, isLight, toggleMode, setThemeMode } = useStorePanelTheme();
  const ui = useMemo(() => getCostPanelTheme('admin', mode), [mode]);
  const muiTheme = useMemo(() => createAdminPanelMuiTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      isLight,
      toggleMode,
      setThemeMode,
      ui,
      whiteButtonSx: {
        color: ui.textPrimary,
        borderColor: ui.buttonBorder,
        backgroundColor: 'transparent',
      },
      selectableCardSx: (selected, { selectedBg = '#1976d2' } = {}) => ({
        border: selected ? '2px solid #4caf50' : isLight ? '1px solid #cbd5e1' : '1px solid #263238',
        backgroundColor: selected ? selectedBg : isLight ? '#f8fafc' : '#1c2a44',
        color: selected ? '#ffffff' : isLight ? ui.textPrimary : '#cfd8dc',
        borderRadius: 1,
        p: 1.5,
      }),
      sortThSx: {
        color: ui.textPrimary,
        '& .MuiTableSortLabel-root': { color: ui.textPrimary },
        '& .MuiTableSortLabel-root.Mui-active': { color: ui.textPrimary },
        '& .MuiTableSortLabel-icon': { color: `${ui.textSecondary} !important` },
      },
      tableRowInteractiveSx: (selected = false) => ({
        cursor: 'pointer',
        '&:hover': { bgcolor: isLight ? 'rgba(15, 23, 42, 0.04)' : '#2a3441' },
        bgcolor: selected ? (isLight ? 'rgba(30, 64, 175, 0.08)' : '#2a3441') : 'transparent',
      }),
      previewPanelSx: {
        p: 2,
        borderRadius: 1,
        minHeight: '60px',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        bgcolor: isLight ? '#f1f5f9' : '#2a3441',
        border: isLight ? '1px solid #cbd5e1' : '1px solid #3a4451',
      },
      listItemPaperSx: (selected, { selectedBg } = {}) => {
        const selBg = selectedBg ?? (isLight ? '#1e40af' : '#1a237e');
        const idleBg = isLight ? '#f8fafc' : '#1c2a44';
        const hoverBg = isLight ? '#eef2f7' : '#263238';
        return {
          p: 2,
          mb: 1,
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'background-color 0.15s ease-in-out',
          border: selected ? '2px solid #4caf50' : isLight ? '1px solid #cbd5e1' : '1px solid #263238',
          bgcolor: selected ? selBg : idleBg,
          color: selected ? '#ffffff' : ui.textPrimary,
          '&:hover': {
            bgcolor: selected ? selBg : hoverBg,
          },
        };
      },
      dialogPaperSx: {
        bgcolor: ui.cardBg,
        color: ui.textPrimary,
        backgroundImage: 'none',
      },
    }),
    [mode, isLight, toggleMode, setThemeMode, ui],
  );

  return (
    <AdminPanelThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </AdminPanelThemeContext.Provider>
  );
}

AdminPanelThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAdminPanelTheme() {
  const ctx = useContext(AdminPanelThemeContext);
  if (!ctx) {
    const ui = getCostPanelTheme('admin', 'dark');
    return {
      mode: 'dark',
      isLight: false,
      toggleMode: () => {},
      setThemeMode: () => {},
      ui,
      whiteButtonSx: {
        color: ui.textPrimary,
        borderColor: ui.buttonBorder,
        backgroundColor: 'transparent',
      },
      selectableCardSx: (selected) => ({
        border: selected ? '2px solid #4caf50' : '1px solid #263238',
        backgroundColor: selected ? '#1976d2' : '#1c2a44',
        color: selected ? '#ffffff' : '#cfd8dc',
        borderRadius: 1,
        p: 1.5,
      }),
      listItemPaperSx: () => ({
        p: 2,
        mb: 1,
        borderRadius: 1,
        border: '1px solid #263238',
        bgcolor: '#1c2a44',
        color: ui.textPrimary,
      }),
    };
  }
  return ctx;
}
